import { animated, useSpring } from '@react-spring/web';
import { css } from 'aphrodite';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { baseStyleSheet } from '../../styles/styles';
import { styleSheet } from './styles';

interface IProps {
	forceClose?: boolean;
	threshold: number;
	onRenderTrigger?: (isExpanded: boolean) => JSX.Element;
}

export const OverflowAccordion: React.FC<React.PropsWithChildren<IProps>> = ({
	children,
	forceClose,
	threshold,
	onRenderTrigger,
}) => {
	const childOuterRef = useRef(null);
	const childInnerRef = useRef(null);
	const [isExpanded, setIsExpanded] = useState(false);
	const [showTrigger, setShowTrigger] = useState(false);
	const [contentHeight, setContentHeight] = useState(0);

	const spring = useSpring({
		config: { friction: 28, tension: 200 },
		maxHeight: isExpanded ? `${contentHeight}px` : `${threshold}px`,
	});

	useEffect(() => {
		// timeout required due to animation
		setTimeout(() => {
			const innerHeight = childInnerRef?.current?.clientHeight || 0;
			setContentHeight(innerHeight);
			setShowTrigger(innerHeight > threshold);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [childInnerRef, childOuterRef, children]);

	useEffect(() => {
		if (forceClose) {
			setIsExpanded(false);
		}
	}, [forceClose]);

	const onTriggerClick = () => {
		setIsExpanded(!isExpanded);
	};

	const renderTrigger = () => {
		return (
			<button className={css(baseStyleSheet.minorActionLink, styleSheet.trigger)} onClick={onTriggerClick}>
				{onRenderTrigger?.(isExpanded) ?? <span>{isExpanded ? 'Collapse' : 'Show all'}</span>}
			</button>
		);
	};

	const AnimatedDiv = animated.div;

	return (
		<div>
			<AnimatedDiv ref={childOuterRef} style={spring} className={css(styleSheet.childOuter)}>
				<div ref={childInnerRef} className={css(styleSheet.childInner)}>
					{children}
				</div>
			</AnimatedDiv>
			{showTrigger && renderTrigger()}
		</div>
	);
};
